import React from "react";
import type { DragSourceMonitor } from "react-dnd";
import { useDrag } from "react-dnd";
import InputIcon from "../../../../../../assets/icons/text.svg";
import TextareaIcon from "../../../../../../assets/icons/textarea.svg";
import CheckboxIcon from "../../../../../../assets/icons/checkbox.svg";
import RadioIcon from "../../../../../../assets/icons/radio.svg";
import { ItemTypes } from "./ItemTypes";

interface DropResult {
  allowedDropEffect: string;
  dropEffect: string;
  name?: string;
  sectionId?: string;
}

/** Labels aligned with SmartCMS advance-create palette */
export const LABEL_MAP: Record<string, string> = {
  section: "Section",
  heading: "Heading",
  text: "Input",
  number: "Number",
  textarea: "Text Area",
  checkbox: "Checkbox",
  file: "Image Upload",
  uploader: "Image Upload",
  select: "Select",
  radio: "Radio",
  date: "Date",
  time: "Time",
  email: "Email",
  phone: "Phone",
  address: "Address",
  button: "Button",
  fillblanks: "Fill Blanks",
  daterange: "Date Range",
  rating: "Rating",
  divider: "Divider",
  project: "Project Interest",
};

const DragHandle = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="currentColor"
    className="shrink-0 text-[#C4C9D4]"
    aria-hidden
  >
    <circle cx="9" cy="6" r="1.5" />
    <circle cx="15" cy="6" r="1.5" />
    <circle cx="9" cy="12" r="1.5" />
    <circle cx="15" cy="12" r="1.5" />
    <circle cx="9" cy="18" r="1.5" />
    <circle cx="15" cy="18" r="1.5" />
  </svg>
);

const baseField = (type: string, extras: Record<string, any> = {}) => ({
  name: LABEL_MAP[type] || type,
  type,
  required: false,
  placeholder: extras.placeholder ?? `Enter ${LABEL_MAP[type] || type}`,
  conditions: [] as any[],
  conditionOperator: "AND" as const,
  icon: InputIcon,
  ...extras,
});

/** Default fields for a Yhataw website landing popup form. */
export const defaultWebsiteFormFields = () => [
  {
    id: "website_name",
    name: "Your Name",
    type: "text",
    required: true,
    placeholder: "Please enter your name",
    conditions: [],
    conditionOperator: "AND",
  },
  {
    id: "website_email",
    name: "Email",
    type: "email",
    required: true,
    placeholder: "Please enter your email",
    conditions: [],
    conditionOperator: "AND",
  },
  {
    id: "website_phone",
    name: "Phone No.",
    type: "phone",
    required: true,
    placeholder: "00000 00000",
    conditions: [],
    conditionOperator: "AND",
  },
  {
    id: "website_unit",
    name: "BHK / Unit Type",
    type: "select",
    required: false,
    placeholder: "Select from this project",
    options: [],
    conditions: [],
    conditionOperator: "AND",
  },
  {
    id: "website_message",
    name: "Message",
    type: "textarea",
    required: false,
    placeholder: "Write a message",
    conditions: [],
    conditionOperator: "AND",
  },
  {
    id: "website_submit",
    name: "Send Message",
    type: "button",
    required: false,
    placeholder: "",
    conditions: [],
    conditionOperator: "AND",
  },
];

/** Field templates — mirrors SmartCMS FormBuilder components list */
export const FIELD_TEMPLATES: Record<string, any> = {
  section: {
    name: "New Section",
    type: "section",
    required: false,
    placeholder: "",
    /** Yhataw: visual group vs multi-page step — default Normal */
    sectionType: "normal",
    /** SmartCMS: sectionAccess */
    access: "all",
    sectionAccess: "all",
    sectionAllowedUsers: [],
    conditions: [],
    conditionOperator: "AND",
    icon: InputIcon,
  },
  heading: baseField("heading", {
    name: "Heading",
    placeholder: "Section heading",
    required: false,
  }),
  text: baseField("text", {
    name: "Input",
    placeholder: "Enter text",
    fieldSubType: "text",
  }),
  number: baseField("number", {
    name: "Number",
    placeholder: "Enter number",
    fieldSubType: "number",
  }),
  textarea: baseField("textarea", {
    name: "Text Area",
    placeholder: "Enter details",
    icon: TextareaIcon,
  }),
  checkbox: baseField("checkbox", {
    name: "Checkbox",
    options: ["Option 1", "Option 2"],
    placeholder: "Select options",
    icon: CheckboxIcon,
    multiple: true,
    choiceStyle: "chips",
    allowOther: false,
  }),
  file: baseField("file", {
    name: "Image Upload",
    placeholder: "Upload image",
  }),
  uploader: baseField("uploader", {
    name: "Image Upload",
    placeholder: "Upload image",
  }),
  select: baseField("select", {
    name: "Select",
    options: ["Option 1", "Option 2"],
    placeholder: "Select option",
    multiple: false,
    choiceStyle: "chips",
    allowOther: false,
  }),
  radio: baseField("radio", {
    name: "Radio",
    options: ["Option 1", "Option 2"],
    placeholder: "Select option",
    icon: RadioIcon,
    choiceStyle: "chips",
    allowOther: false,
  }),
  date: baseField("date", {
    name: "Date",
    placeholder: "Select date",
  }),
  time: baseField("time", {
    name: "Time",
    placeholder: "Select time",
  }),
  email: baseField("email", {
    name: "Email",
    placeholder: "name@example.com",
  }),
  phone: baseField("phone", {
    name: "Phone",
    placeholder: "Phone number",
  }),
  address: baseField("address", {
    name: "Address",
    placeholder: "Enter address",
  }),
  button: baseField("button", {
    name: "Submit",
    placeholder: "",
    required: false,
  }),
  fillblanks: baseField("fillblanks", {
    name: "Fill Blanks",
    placeholder: "Type ___ for blanks",
  }),
  daterange: baseField("daterange", {
    name: "Date Range",
    placeholder: "Start – End",
  }),
  rating: baseField("rating", {
    name: "Rating",
    placeholder: "",
    maxRating: 5,
    /** Public form: animated emoji faces vs classic stars */
    ratingStyle: "emoji",
  }),
  project: baseField("project", {
    name: "Project Interest",
    placeholder: "Select project(s)",
    required: false,
    selectionMode: "single",
    projectScope: "all",
    allowedProjectIds: [],
    icon: RadioIcon,
  }),
  divider: baseField("divider", {
    name: "Divider",
    placeholder: "",
    required: false,
  }),
};

/** SmartCMS-style: group flat fields by section markers (order = ownership). */
export const buildWorkflowSteps = (fields: any[]) => {
  const list = Array.isArray(fields) ? fields : [];
  const steps: { label: string; section: any | null; fields: any[] }[] = [];
  let current: { label: string; section: any | null; fields: any[] } | null =
    null;
  const preamble: any[] = [];

  for (const field of list) {
    if (field?.type === "section") {
      current = {
        label: field.name || field.label || "New Section",
        section: field,
        fields: [],
      };
      steps.push(current);
      continue;
    }
    if (field?.type === "button" || field?.type === "divider") continue;
    if (!current) {
      preamble.push(field);
    } else {
      current.fields.push(field);
    }
  }

  if (preamble.length) {
    if (steps.length) {
      steps[0].fields = [...preamble, ...steps[0].fields];
    } else {
      steps.push({ label: "Form", section: null, fields: preamble });
    }
  }

  return steps;
};

/** Preview grouping: every section owns the fields under it (includes heading, divider, button). */
export const buildFormSections = (fields: any[]) => {
  const list = Array.isArray(fields) ? fields : [];
  const sections: { label: string; section: any | null; fields: any[] }[] = [];
  let current: { label: string; section: any | null; fields: any[] } | null =
    null;
  const preamble: any[] = [];

  for (const field of list) {
    if (field?.type === "section") {
      current = {
        label: field.name || field.label || "New Section",
        section: field,
        fields: [],
      };
      sections.push(current);
      continue;
    }
    if (!current) {
      preamble.push(field);
    } else {
      current.fields.push(field);
    }
  }

  if (preamble.length) {
    sections.unshift({ label: "", section: null, fields: preamble });
  }

  return sections;
};

/** Resolve which org projects appear for a project-interest field in builder preview. */
export const resolvePreviewProjectOptions = (
  field: any,
  availableProjects: { id: string; label: string }[] = []
) => {
  if (field?.type !== "project") return [];
  const scope = field.projectScope === "selected" ? "selected" : "all";
  let projects = availableProjects;
  if (
    scope === "selected" &&
    Array.isArray(field.allowedProjectIds) &&
    field.allowedProjectIds.length
  ) {
    const allowed = new Set(field.allowedProjectIds.map(String));
    projects = availableProjects.filter((p) => allowed.has(String(p.id)));
  }
  return projects;
};

export const createFieldFromType = (
  fieldName: string,
  idPrefix = "fld"
) => {
  const template = FIELD_TEMPLATES[fieldName] || FIELD_TEMPLATES.text;
  return {
    ...template,
    id: `${idPrefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    text: fieldName,
  };
};

/** Insert a new field into the flat items list (optionally inside a section). */
export const appendFieldToItems = (
  prevItems: any[],
  fieldName: string,
  sectionId?: string,
  idPrefix = "fld"
) => {
  const newItem = createFieldFromType(fieldName, idPrefix);
  const list = Array.isArray(prevItems) ? prevItems : [];

  if (fieldName === "section" || !sectionId) {
    return [...list, newItem];
  }

  const secIdx = list.findIndex((f: any) => f.id === sectionId);
  if (secIdx < 0) return [...list, newItem];

  let insertAt = secIdx + 1;
  while (insertAt < list.length && list[insertAt]?.type !== "section") {
    insertAt += 1;
  }
  const next = [...list];
  next.splice(insertAt, 0, newItem);
  return next;
};

export const BoxGrid = (props: any) => {
  const generateId = React.useId();
  const didDragRef = React.useRef(false);

  const appendField = React.useCallback(
    (fieldName: string, sectionId?: string) => {
      props.setItems((prevItems: any[]) =>
        appendFieldToItems(prevItems, fieldName, sectionId, generateId)
      );
    },
    [generateId, props]
  );

  const [{ opacity, isDragging }, drag] = useDrag(
    () => ({
      type: ItemTypes.BOX,
      item: () => {
        didDragRef.current = true;
        return { name: props.name };
      },
      // Field is added in Dustbin / SectionDropZone drop handlers (more reliable).
      // Keep end() as a fallback when dropResult is returned without drop-side mutate.
      end(item, monitor) {
        const didDrop = monitor.didDrop();
        const dropResult = monitor.getDropResult() as DropResult | null;
        if (!item || !didDrop) return;
        // Drop targets already mutated items when dropHandled is set
        if ((dropResult as any)?.dropHandled) return;
        if (!dropResult) return;
        appendField(item.name, dropResult.sectionId);
      },
      collect: (monitor: DragSourceMonitor) => ({
        opacity: monitor.isDragging() ? 0.45 : 1,
        isDragging: monitor.isDragging(),
      }),
    }),
    [props?.name, appendField]
  );

  const label = LABEL_MAP[props?.name] || props?.name;

  return (
    <div
      role="button"
      tabIndex={0}
      ref={drag as any}
      style={{ opacity }}
      onClick={() => {
        // Ignore click that follows a drag gesture
        if (didDragRef.current) {
          didDragRef.current = false;
          return;
        }
        appendField(props.name);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          appendField(props.name);
        }
      }}
      className={`flex w-full cursor-grab items-center gap-2 rounded-lg border border-[#EEF0F5] bg-white px-2.5 py-1.5 text-left shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition hover:border-[#D4B8C0] hover:bg-[#F8FAFF] active:cursor-grabbing select-none ${
        isDragging ? "shadow-md ring-2 ring-[#841C39]/30 opacity-50" : ""
      }`}
    >
      <span className="pointer-events-none flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#F9F0F4]">
        <img src={props.icon || InputIcon} alt="" className="h-3.5 w-3.5" />
      </span>
      <span className="pointer-events-none min-w-0 flex-1 text-[13px] font-semibold leading-none text-[#111827]">
        {label}
      </span>
      <span className="pointer-events-none">
        <DragHandle />
      </span>
    </div>
  );
};
